import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  Bookmark,
  BookmarkPlus,
  Check,
  ChevronDown,
  Loader2,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';

/**
 * Menu déroulant des filtres sauvegardés du module Équipements (US-EQ-12).
 *
 *  - Lit `/equipements/saved-filters` à l'ouverture.
 *  - Click sur un filtre → applique `payload` à la page via `onApply`.
 *  - Action "Sauvegarder le filtre actuel" → mini-modal nom + checkbox défaut.
 *  - Action "Supprimer" sur chaque ligne.
 */

export interface SavedFilter {
  id: string;
  module: string;
  nom: string;
  filtres: Record<string, unknown>;
  is_default: boolean;
  created_at: string;
}

export function SavedFilterMenu({
  currentFilters,
  onApply,
}: {
  currentFilters: Record<string, unknown>;
  onApply(filters: Record<string, unknown>): void;
}) {
  const [items, setItems] = useState<SavedFilter[]>([]);
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  async function refresh() {
    try {
      const res = await api.get<{ data: SavedFilter[] }>(
        '/equipements/saved-filters?module=equipements',
      );
      setItems(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  // Ferme au click extérieur.
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  async function handleApply(filter: SavedFilter) {
    setActiveId(filter.id);
    onApply(filter.filtres);
    setOpen(false);
  }

  async function handleDelete(id: string) {
    if (!confirm('Supprimer ce filtre sauvegardé ?')) return;
    try {
      await api.delete(`/equipements/saved-filters/${id}`);
      if (activeId === id) setActiveId(null);
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50"
      >
        <Bookmark className="w-3.5 h-3.5 mr-1.5" />
        Filtre sauvegardé
        <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-30 max-h-96 overflow-y-auto">
          {errorMessage && (
            <div className="m-2 rounded-lg border border-red-200 bg-red-50 p-2 flex items-start space-x-2">
              <AlertCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">{errorMessage}</p>
            </div>
          )}

          {items.length === 0 ? (
            <p className="px-3 py-3 text-xs text-gray-500 italic">Aucun filtre sauvegardé.</p>
          ) : (
            <ul className="py-1">
              {items.map((f) => (
                <li
                  key={f.id}
                  className="flex items-center justify-between px-3 py-1.5 hover:bg-gray-50 group"
                >
                  <button
                    type="button"
                    onClick={() => handleApply(f)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="flex items-center text-sm text-gray-800">
                      {f.is_default && (
                        <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 mr-1" />
                      )}
                      <span className="truncate">{f.nom}</span>
                      {activeId === f.id && (
                        <Check className="w-3 h-3 text-green-600 ml-1.5 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(f.id)}
                    className="p-1 rounded text-red-600 opacity-0 group-hover:opacity-100"
                    aria-label="Supprimer"
                    title="Supprimer"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="border-t border-gray-100 p-2">
            <button
              type="button"
              onClick={() => {
                setCreateOpen(true);
                setOpen(false);
              }}
              className="w-full inline-flex items-center px-2 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded"
            >
              <BookmarkPlus className="w-3.5 h-3.5 mr-1.5" />
              Sauvegarder le filtre actuel
            </button>
          </div>
        </div>
      )}

      {createOpen && (
        <CreateFilterModal
          currentFilters={currentFilters}
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function CreateFilterModal({
  currentFilters,
  onClose,
  onSaved,
}: {
  currentFilters: Record<string, unknown>;
  onClose(): void;
  onSaved(): void;
}) {
  const [nom, setNom] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!nom.trim()) {
      setErrorMessage('Le nom est requis.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post('/equipements/saved-filters', {
        module: 'equipements',
        nom: nom.trim(),
        filtres: currentFilters,
        is_default: isDefault,
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Sauvegarder le filtre</h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={submit} className="p-4 space-y-3">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Nom *</label>
              <input
                autoFocus
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Mes équipements vitaux en panne"
                required
              />
            </div>
            <label className="flex items-center space-x-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>Définir comme filtre par défaut</span>
            </label>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Sauvegarder
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}
